import { type FormEvent, useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useShiftAuth } from '../../contexts/ShiftAuthProvider';
import { shiftEmployeesService } from '../../services/employees/shift-employees.service';
import type { ShiftEmployeeSelfProfile } from '../../models/employees.types';
import {
  type EmployeeFormState,
  type ShiftSectionOption,
} from '../../components/EmployeeFormFields/employee-form.types';
import {
  toFormState,
  toSectionOptions,
  buildEmployeeUpdatePayload,
} from '../../components/EmployeeFormFields/employee-form.utils';
import { resolveRequestErrorMessage } from '../../utils/request-errors.utils';

export function useProfilePageController() {
  const { user, refreshMe } = useShiftAuth();

  const isManager = user?.role === 'manager';

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<ShiftEmployeeSelfProfile | null>(null);
  const [form, setForm] = useState<EmployeeFormState | null>(null);
  const [sectionOptions, setSectionOptions] = useState<ShiftSectionOption[]>([]);

  const profileWriteInFlightRef = useRef(false);

  const loadProfile = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const payload = await shiftEmployeesService.getMyProfile();
      setProfile(payload);
      setForm(toFormState(payload));

      if (isManager) {
        const sections = await shiftEmployeesService.listSections();
        setSectionOptions(toSectionOptions(sections));
      } else {
        setSectionOptions([]);
      }
    } catch (requestError: unknown) {
      setError(resolveRequestErrorMessage(requestError, 'לא ניתן לטעון את הפרופיל כרגע.'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadProfile();
  }, [isManager]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form || !profile || profileWriteInFlightRef.current) {
      return;
    }

    const payload = buildEmployeeUpdatePayload(form, isManager, toFormState(profile));
    if (Object.keys(payload).length === 0) {
      return;
    }

    profileWriteInFlightRef.current = true;
    setError(null);
    setIsSaving(true);

    try {
      const updated = await shiftEmployeesService.updateMyProfile(payload);
      setProfile({
        ...updated,
        photoUrl: updated.photoUrl ?? profile?.photoUrl ?? null,
      });
      setForm(toFormState(updated));
      notifications.show({ color: 'green', message: 'הפרופיל נשמר בהצלחה.' });
      await refreshMe();
    } catch (requestError: unknown) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: resolveRequestErrorMessage(requestError, 'שמירת הפרופיל נכשלה.'),
      });
    } finally {
      profileWriteInFlightRef.current = false;
      setIsSaving(false);
    }
  };

  return {
    user,
    isManager,
    isLoading,
    isSaving,
    error,
    profile,
    form,
    setForm,
    sectionOptions,
    loadProfile,
    onSubmit,
  };
}
